import type { FunctionComponent } from "react";
import { useEffect, useRef, useState, useCallback } from "react";

import { swSettings } from "app/common/services/swSettings";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { ButtonsContainer } from "pro-features/components/Rule/src/styledComponents";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import type { IGenderDistribution } from "app/pages/website-analysis/demographics/types";
import { PdfExportService } from "app/services/PdfExportService";
import { allTrackers } from "app/services/track/track";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import Chart from "pro-features/components/Chart/src/Chart";
import {
    TitleContainer,
    StyledHeaderTitle,
    ChartContainer,
    ChartWrapper,
    ChartAndLegendsWrapper,
    LoaderContainer,
} from "./StyledComponents";

import { CHART_CATEGORIES, getBarChartConfig, multipleDomainsTooltipFormatter } from "./GenderBarChartComparedConfig";

import { DemographicsChannelsLegends } from "../DemographicsChannelsLegends";
import { StyledLegendsWrapper } from "../StyledComponents";

import { Loader } from "app/pages/website-analysis/traffic-sources/mmx/components/common/Loader";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { sortArrayAsOtherArray } from "app/UtilitiesAndConstants/UtilityFunctions/sort";
import { getExcelDownloadTracking, getExelLink } from "app/pages/website-analysis/demographics/utils";
import { DEFAULT_WEBSITES_DELIMITER } from "app/services/sitesService/consts";

type IDomain = string;
export type IGenderDistributionData = Record<IDomain, IGenderDistribution>;

interface IGenderDistributionBarChartComparedProps {
    data: IGenderDistributionData;
    isLoading?: boolean;
    disableAddToDashboard?: boolean;
    downloadExcelUrl?: string;
}

export const GenderDistributionBarChartCompared: FunctionComponent<IGenderDistributionBarChartComparedProps> = (
    props,
) => {
    const { data, isLoading = false, disableAddToDashboard = false, downloadExcelUrl } = props;
    const chartRef = useRef<HTMLElement>(null);
    const params = proNavigator.getParams() as any;
    const { key } = params;
    const keys = key.split(DEFAULT_WEBSITES_DELIMITER);
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const apiParams = proNavigator.getApiParams(params);
    const [isEmpty, setIsEmpty] = useState(true);
    const [graphData, setGraphData] = useState([]); // data (or filtered data) for the chart
    const [fullGraphData, setFullGraphData] = useState([]); // data for the legends

    const lagendToggleHandler = useCallback((filterData) => {
        setGraphData(filterData);
    }, []);

    // on first load we want to set the chart and legends with the same filtered data.
    useEffect(() => {
        if (!data) {
            return;
        }

        const initGraphData = comparisonColumnChartData();
        setGraphData(initGraphData);
        setFullGraphData(initGraphData);
    }, [isLoading]);

    const getPNG = useCallback((): Promise<void> => {
        allTrackers.dispatchTrackEvent("Gender_Distribution.png", "submit-ok", "Gender_Distribution.png");
        const offSetX = 0;
        const offSetY = 50;
        const fileName = `Website Analysis Gender-Distribution-(${apiParams.key})-(from: ${apiParams.from})-(to: ${apiParams.to})`;
        const styleHTML = PdfExportService.getStylesheetsForPng();
        return PdfExportService.downloadHtmlPngFedService(
            styleHTML + chartRef.current.outerHTML,
            fileName,
            chartRef.current.offsetWidth + offSetX,
            chartRef.current.offsetHeight + offSetY,
        );
    }, [chartRef]);

    const getUtilityButtons = () => {
        return (
            <ButtonsContainer>
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={getPNG}
                />
                {!disableAddToDashboard ? (
                    <AddToDashboard
                        modelType="fromWebsite"
                        webSource={"Total"}
                        type="BarChartDemographicsGender"
                        metric={"WebDemographicsGender"}
                    />
                ) : null}

                <DownloadButtonMenu
                    Excel={true}
                    downloadUrl={downloadExcelUrl ?? getExelLink(params)}
                    excelLocked={!downloadExcelPermitted}
                    trackingFunction={getExcelDownloadTracking("Gender")}
                />
            </ButtonsContainer>
        );
    };

    const comparisonColumnChartData = () => {
        let isEmptyFlag = true;
        return Object.entries(data).map(([site, data], index) => {
            return {
                name: site,
                data: sortArrayAsOtherArray(Object.keys(data), CHART_CATEGORIES).map((key) => {
                    const bucket = key;
                    const val = data[key];
                    if (val && isEmptyFlag) {
                        // this will run only once and change the isEmpty flag if needed
                        isEmptyFlag = false;
                        setIsEmpty(false);
                    }
                    return {
                        key: bucket,
                        color: CHART_COLORS.chartMainColors[index],
                        isSite: true,
                        opacity: 1,
                        y: val,
                        dataLabels: {
                            enabled: true,
                        },
                    };
                }),
            };
        });
    };

    return (
        <ChartContainer ref={chartRef}>
            <TitleContainer>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={i18nFilter()("webdemographics.piechart.title.tooltip")}>
                            {i18nFilter()("dashboard.metricGallery.Website.DesktopDemographicsGender.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                </FlexColumn>
                {getUtilityButtons()}
            </TitleContainer>
            {isLoading ? (
                <LoaderContainer>
                    <Loader />
                </LoaderContainer>
            ) : (
                <>
                    {isEmpty ? (
                        <TableNoData
                            icon="no-data"
                            messageTitle={i18nFilter()("global.nodata.notavilable")}
                        />
                    ) : (
                        <ChartAndLegendsWrapper>
                            <ChartWrapper>
                                <Chart
                                    type={"column"}
                                    config={getBarChartConfig({
                                        filter: [percentageSignFilter, 1],
                                        tooltipFormatter: multipleDomainsTooltipFormatter,
                                    })}
                                    data={graphData}
                                />
                            </ChartWrapper>

                            <StyledLegendsWrapper>
                                <DemographicsChannelsLegends
                                    data={fullGraphData}
                                    keys={keys}
                                    onLegendToggle={lagendToggleHandler}
                                />
                            </StyledLegendsWrapper>
                        </ChartAndLegendsWrapper>
                    )}
                </>
            )}
        </ChartContainer>
    );
};
